import {
  Button,
  Container,
  Group,
  SimpleGrid,
  Text,
  Textarea,
  TextInput,
  Title,
  Box,
  rem,
  Paper,
} from "@mantine/core";
import { IconSend } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useForm } from "@mantine/form";
import { useState } from "react";

import { GetInTouchIconsList } from "./Icons";
import { Colors } from "../../../Lib/Data";
import {
  DefaultSuccessNotification,
  validateEmail,
} from "../../../Lib/Methods";
import { PerformRequest } from "../../../Lib/usePerformRequest";
import { Endpoints } from "../../../Lib/Endpoints";

export default function HomeGetInTouch() {
  const form = useForm({
    initialValues: {
      email: "",
      name: "",
      subject: "",
      message: "",
    },
    validate: {
      email: (value) => (validateEmail(value) ? null : "Invalid email"),
      name: (value) => (value && value.length > 3 ? null : "Name is required"),
      message: (value) =>
        value && value.length > 5 ? null : "Message is required",
      subject: (value) =>
        value && value.length > 5 ? null : "Subject is required",
    },
  });

  const [isLoading, setLoading] = useState(false);

  const SendMessage = async () => {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      setLoading(true);
      const { email, name, subject, message } = form.values;
      const r = await PerformRequest({
        route: Endpoints.GetSendEmailFromHomepage(),
        method: "POST",
        data: {
          email,
          name,
          subject,
          message,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r) {
        if (r.status === 200) {
          form.reset();
          DefaultSuccessNotification(
            "Message sent successfully!",
            "We will get back to you soon"
          );
        }
        if (r.status === 500) {
          DefaultSuccessNotification(
            "Message not sent",
            "Please try again later"
          );
        }
      } else {
        DefaultSuccessNotification(
          "Message not sent",
          "Please try again later"
        );
      }
    }
  };

  const inputProps = {
    variant: "filled",
    radius: "md",
    size: "md",
  };

  return (
    <Container size="lg" id="contact" py={80} mb={50}>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <Paper shadow="xl" radius="lg" withBorder>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={0}>
            {/* Left Column: Contact Info */}
            <Box
              p={50}
              style={{
                background: `linear-gradient(135deg, ${Colors.GreenSecondary} 0%, #2f9e44 100%)`,
                borderTopLeftRadius: "var(--mantine-radius-lg)",
                borderBottomLeftRadius: "var(--mantine-radius-lg)",
                color: "white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: 500,
              }}
            >
              <div>
                <Title order={2} c="white" mb="sm" fw={800}>
                  Contact Information
                </Title>
                <Text c="white" opacity={0.8} mb={50} fz="lg">
                  Ask us anything or just say hi...
                </Text>

                <GetInTouchIconsList variant="white" />
              </div>

              {/* Decorative element or additional info could go here */}
            </Box>

            {/* Right Column: Form */}
            <Box
              p={50}
              bg="white"
              style={{
                borderTopRightRadius: "var(--mantine-radius-lg)",
                borderBottomRightRadius: "var(--mantine-radius-lg)",
              }}
            >
              <form onSubmit={(event) => event.preventDefault()}>
                <Title order={3} c={Colors.GreenSecondary} mb="xl" fw={700}>
                  Get in touch
                </Title>

                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <TextInput
                    label="Name"
                    placeholder="Your name"
                    {...inputProps}
                    {...form.getInputProps("name")}
                  />
                  <TextInput
                    label="Email"
                    placeholder="your@email.com"
                    {...inputProps}
                    {...form.getInputProps("email")}
                  />
                </SimpleGrid>

                <TextInput
                  mt="md"
                  label="Subject"
                  placeholder="Subject"
                  {...inputProps}
                  {...form.getInputProps("subject")}
                />

                <Textarea
                  mt="md"
                  label="Message"
                  placeholder="How can we help?"
                  minRows={4}
                  {...inputProps}
                  {...form.getInputProps("message")}
                />

                <Group justify="flex-end" mt="xl">
                  <Button
                    type="submit"
                    size="md"
                    radius="md"
                    color={Colors.GreenSecondary}
                    onClick={SendMessage}
                    loading={isLoading}
                    leftSection={<IconSend size={18} />}
                  >
                    Send Message
                  </Button>
                </Group>
              </form>
            </Box>
          </SimpleGrid>
        </Paper>
      </motion.div>
    </Container>
  );
}
